import { yupResolver } from '@hookform/resolvers/yup'
import axios from 'axios'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { NavLink } from 'react-router-dom'
import { Alert } from '../../components/Alert'
import { Button } from '../../components/Button'
import { Input } from '../../components/FormElements/Input'
import { PasswordInput } from '../../components/FormElements/PasswordInput'
import { useAuth } from '../../contexts/AuthContext'
import { ErrorCode } from '../../enumns/errors'
import { createLoginSchema } from '../../validators/loginSchema'

interface FormValues {
  email: string
  password: string
}

const schema = createLoginSchema({
  email: {
    email: 'INVALID_EMAIL',
    required: 'REQUIRED_FIELD',
  },
  password: {
    required: 'REQUIRED_FIELD',
  },
})

export const Login = () => {
  const [intlErrorCode, setIntlErrorCode] = useState<string | null | undefined>()

  const { signIn } = useAuth()
  const { formatMessage } = useIntl()
  const { control, handleSubmit, formState } = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const onSignIn = async (data: FormValues) => {
    try {
      await signIn(data, (authUserType) => {
        if (authUserType !== 'expert') {
          setIntlErrorCode(ErrorCode.NO_PERMISSION_ACCESS_PANEL_EXPERT)
          return false
        }
        return true
      })
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          setIntlErrorCode(ErrorCode.INVALID_CREDENTIALS)
          return
        }
      }
      setIntlErrorCode(ErrorCode.UNKNOWN)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="bg-white w-[500px] h-[477px] p-20 rounded-lg shadow-lg text-center flex flex-col justify-center">
        <h2 className="text-3xl font-normal text-gray-800 mb-8 text-left">
          Login
        </h2>

        {intlErrorCode && (
          <div className="mb-6">
            <Alert
              fullWidth
              variant="error"
              onClose={() => setIntlErrorCode(null)}
            >
              {formatMessage({
                id: intlErrorCode,
                defaultMessage: ErrorCode.UNKNOWN,
              })}
            </Alert>
          </div>
        )}

        <form onSubmit={handleSubmit(onSignIn)} className="flex flex-col">
          <div className="mb-8 text-left tracking-wider">
            <Controller
              name="email"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { error, invalid, isDirty },
              }) => (
                <Input
                  label="E-MAIL"
                  placeholder="********"
                  className="text-base font-semibold text-gray-700"
                  value={value}
                  onChange={onChange}
                  errorMessage={
                    error?.message && formatMessage({ id: error?.message })
                  }
                  isValid={!invalid && isDirty}
                />
              )}
            />
          </div>

          <div className="mb-4 text-left tracking-wider">
            <Controller
              name="password"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { error, invalid },
              }) => (
                <PasswordInput
                  id="password"
                  label={formatMessage({ id: 'PASSWORD_FIELD' })}
                  placeholder="********"
                  className="text-base font-semibold text-gray-700"
                  value={value}
                  onChange={onChange}
                  errorMessage={
                    error?.message && formatMessage({ id: error?.message })
                  }
                  isValid={!invalid}
                />
              )}
            />
          </div>

          <div className="mb-6 text-right">
            <NavLink
              to="/expert/forgot-password"
              className="text-[0.7rem] font-medium text-gray-800 hover:underline hover:decoration-[#44A0C1] hover:underline-offset-2"
            >
              <FormattedMessage id="FORGOT_PASSWORD_LINK_TEXT" />
            </NavLink>
          </div>

          <Button
            isLoading={formState.isSubmitting}
            type="submit"
            disabled={!formState.isValid}
            fullWidth
            variant="primary"
            size="small"
            borderRadius="no-rounded"
          >
            {formatMessage({ id: 'SIGN_IN' })}
          </Button>

          <p className="mt-4 text-sm text-gray-800">
            <FormattedMessage id="CREATE_ACCOUNT_TEXT" />{' '}
            <NavLink
              to="/expert/register"
              className="font-medium text-primary-light hover:underline"
            >
              {formatMessage({ id: 'CREATE_ACCOUNT_LINK_TEXT' })}
            </NavLink>
          </p>
        </form>
      </div>
    </div>
  )
}
